/*
 * CONTRARIAN LAYER — Semantic Interpreter
 *
 * Translates V6 engine output into a personal narrative.
 * Lens: the new economy — what AI creates, not just what it replaces.
 *
 * When claudeReading is present in the payload, it is used for all
 * narrative sections. The Python engine still provides dimensions
 * for classification and Three.js visual signals.
 */

/* ── POSITION CLASSIFIER ──────────────────────────────────────────── */

function classifyPosition(out, answers) {
  const ai = out?.aiAssessment || {}
  const human = out?.humanAdvantageAssessment || {}
  const conf = out?.confidence || {}
  const limits = out?.limitations || {}

  const overall = (conf?.overall || 'low').toLowerCase()
  const gaps = (limits?.evidenceGaps || [])

  const aiSubLow =
    ai?.aiSubstitutability?.confidence === 'low' &&
    !ai?.aiSubstitutability?.assessment?.toLowerCase().includes('cannot')

  const humanEstablished =
    human?.humanScarcity?.confidence !== 'low' ||
    human?.humanJudgment?.confidence !== 'low' ||
    human?.humanTrust?.confidence !== 'low'

  const hasAiCapabilityAnswer = (answers?.['ai-capability'] || '').length > 20
  const hasHumanAdvantageAnswer = (answers?.['human-advantage'] || '').length > 20

  if (overall === 'high' && humanEstablished) return 'DEFENDED'
  if (overall === 'high' && !humanEstablished) return 'EXPOSED'
  if (overall === 'medium') return 'PARTIAL'
  if (hasAiCapabilityAnswer && !hasHumanAdvantageAnswer) return 'EXPOSED'
  if (hasHumanAdvantageAnswer) return 'PARTIAL'
  if (gaps.length > 10) return 'UNCLEAR'
  return 'UNCERTAIN'
}

/* ── POSITION READING ─────────────────────────────────────────────── */

function buildPositionReading(position, out, answers) {
  const role = answers?.role || 'your current work'
  const industry = answers?.industry || 'your sector'

  const readings = {
    DEFENDED: `The pattern your answers reveal is one of accumulated specificity — what you described as your contribution is not easily handed off to a system, because it depends on context, trust, and judgment that is yours and not transferable. That does not mean the landscape is static. The shift happening in the economy is not only about what AI replaces — it is about what it makes newly possible. The question the pattern in your answers opens is where specifically your kind of contribution fits into what is being built, not just what is being automated.`,

    EXPOSED: `What you described doing in ${role.length > 3 ? role : 'your work'} within ${industry.length > 3 ? industry : 'your sector'} is in a moment of structural change — not because of anything you did or did not do, but because of where AI is moving fastest. That is a description of the landscape, not a verdict on you. The more interesting question your answers open is what that movement creates space for — because the shift is not only taking things away, it is also changing what becomes scarce and therefore valuable.`,

    PARTIAL: `Your answers point to a picture that is still forming — some of what you described is in motion, some carries the kind of specificity that does not transfer easily. That is not unusual. Most people working in the economy right now are in the middle of something that has not finished moving yet. The question the pattern opens is which part of what you described you want to build toward, because the two sides of the picture are pulling in different directions.`,

    UNCLEAR: `The picture the examination can build from your answers is still open — not because there is nothing there, but because the most important signals do not live in role titles or industry categories. They live in how you work, what you notice before others do, what problems find you. The reading will sharpen when those things get named directly. The next version of this conversation starts there.`,

    UNCERTAIN: `The picture is still forming. That is fine — this examination is a starting point, not a final verdict. What tends to sharpen the reading most is naming the specific things shifting in your actual work, and naming what you sense remains distinctly yours in it. Not categories. Specific things. The pattern becomes clearer when those get named.`
  }

  return readings[position] || readings.UNCERTAIN
}

/* ── AI EXPOSURE ANALYSIS ─────────────────────────────────────────── */

function buildAiExposure(out, answers) {
  const aiCapabilityAnswer = answers?.['ai-capability'] || ''
  const ai = out?.aiAssessment || {}
  const autoAssessment = ai?.automationExposure?.assessment || ''

  const lines = []

  if (aiCapabilityAnswer.length > 10) {
    lines.push({
      label: 'What you named as shifting',
      text: aiCapabilityAnswer,
      source: 'your answer'
    })
  }

  if (autoAssessment && !autoAssessment.toLowerCase().includes('not established')) {
    lines.push({
      label: 'What is moving in this landscape',
      text: autoAssessment,
      source: 'engine'
    })
  } else if (aiCapabilityAnswer.length > 10) {
    lines.push({
      label: 'The context',
      text: 'The shift you described is happening across this kind of work broadly — not to you specifically, but to the category. That is worth understanding precisely, because it changes what becomes scarce and therefore where the weight of contribution moves.',
      source: 'engine'
    })
  }

  return lines
}

/* ── HUMAN SIGNAL (replaces defense) ─────────────────────────────── */

function buildDefense(out, answers) {
  const humanAdvantageAnswer = answers?.['human-advantage'] || ''
  const strengthsAnswer = answers?.strengths || ''
  const human = out?.humanAdvantageAssessment || {}

  const hJudgment = human?.humanJudgment
  const hTrust = human?.humanTrust

  const lines = []

  if (humanAdvantageAnswer.length > 10) {
    lines.push({
      label: 'What you named as distinctly yours',
      text: humanAdvantageAnswer,
      source: 'your answer',
      highlight: true
    })
  }

  if (strengthsAnswer.length > 10) {
    lines.push({
      label: 'What you described as your particular capability',
      text: strengthsAnswer,
      source: 'your answer'
    })
  }

  const engineFindings = []

  if (hJudgment?.assessment && !hJudgment.assessment.toLowerCase().includes('no explicit')) {
    engineFindings.push({ label: 'What the pattern suggests', text: hJudgment.assessment })
  }
  if (hTrust?.assessment && !hTrust.assessment.toLowerCase().includes('no explicit')) {
    engineFindings.push({ label: 'The relational dimension', text: hTrust.assessment })
  }

  if (engineFindings.length > 0) {
    lines.push(...engineFindings.map(f => ({ ...f, source: 'engine' })))
  }

  if (lines.length === 0) {
    lines.push({
      label: 'Still forming',
      text: 'The most important part of this reading — what you sense remains distinctly yours — is still open. That is where the examination continues. What the new economy increasingly pays for is judgment, synthesis, and relational depth that cannot be systematized. The next step is naming, as specifically as possible, what that looks like in your actual work.',
      source: 'engine'
    })
  }

  return lines
}

/* ── STRATEGIC QUESTIONS (new economy lens) ───────────────────────── */

const NEW_ECONOMY_QUESTION_MAP = {
  currentEconomicContribution: 'What specifically do you contribute that could not be generated by a system, even in principle — not because the system lacks capability, but because the value lives in the act of a person doing it?',
  informationAdvantage: 'Where do you hold a read on a situation — a room, a relationship, a trend — that no one else in the room has?',
  complexityPremium: 'Which decisions in your work genuinely require someone to be present in the way you are present — not just technically capable?',
  accumulatedCapital: 'What pattern library or relationship network have you built that would take years to reconstruct, and that is actively being relied upon?',
  reproducibility: 'Which outputs you produce today could be generated by a well-prompted system in 18 months — and have you already started treating those as low-leverage work?',
  humanScarcity: 'What would actually break — not just become inconvenient — if you specifically were replaced, versus any competent person with your credentials?',
  humanJudgment: 'Where in your work does the right answer depend on reading a situation that no model has been trained on yet?',
  humanTrust: 'Who specifically relies on you because you are you — and what would it take for that reliance to transfer to something automated?',
  strategicAgency: 'Are you directing the definition of problems, or solving problems others have already defined? That distinction is growing in economic consequence.',
  autonomy: 'How much of your working time is spent on work you chose versus work assigned — and is that ratio moving toward or away from where you want your economic position to be?',
  leverage: 'Does your contribution multiply through others or through outcomes, or is it confined to your own direct output?',
  economicMobility: 'If your current sector compressed by 40% in the next three years, what adjacent position could your specific judgment occupy?',
  positionRisk: 'What specific development — a new model, a platform shift, a market restructuring — would most directly change the value of what you currently do?',
  legacyExposure: 'How much of what you are currently paid for depends on institutional structures — credentials, hierarchies, seniority — that AI is making less relevant?',
  desiredPosition: 'Is the position you want to move toward more or less aligned with the new economy\'s actual value categories: innovation, synthesis, judgment, relational depth?'
}

function buildStrategicQuestions(out) {
  const limits = out?.limitations || {}
  const gaps = (limits?.evidenceGaps || []).filter(g => NEW_ECONOMY_QUESTION_MAP[g])

  return gaps.slice(0, 5).map(g => ({
    field: g,
    question: NEW_ECONOMY_QUESTION_MAP[g]
  }))
}

/* ── DIRECTIONAL READING ──────────────────────────────────────────── */

function buildDirection(position, out, answers) {
  const desiredAnswer = answers?.desired || ''

  const directions = {
    DEFENDED: {
      headline: 'The pattern in your answers points toward what the new economy is building.',
      body: desiredAnswer.length > 10
        ? `You described wanting to move toward: "${desiredAnswer}." The pattern in your answers — what you said you do, what others rely on you for, what you named as distinctly yours — points toward contributions that the new economy is not making easier to replace, it is making more valuable. The question is how deliberately to build in that direction. The landscape is shifting fast enough that drift is a real cost — even for people who are well-positioned today.`
        : 'The pattern in your answers points toward the kind of contribution the new economy values — judgment, trust, contextual reading that does not systematize. The question is how deliberately to build in that direction. The landscape is moving fast enough that staying still has its own cost, even when things look stable.'
    },

    EXPOSED: {
      headline: 'What is shifting in your landscape is also opening something.',
      body: desiredAnswer.length > 10
        ? `You described wanting to move toward: "${desiredAnswer}." The shift happening in the work you described is structural — not personal, not a result of anything you did or did not do. The same movement that is changing what AI handles is also changing what becomes scarce. What opens is the question of where specifically your kind of contribution fits in what is being built next.`
        : 'What is shifting in the work you described is happening broadly across this kind of role. That is not a statement about you — it is a description of the landscape. The shift also changes what becomes scarce. The more interesting question your answers open is where your particular contribution fits in what the economy is building, not just what it is automating.'
    },

    PARTIAL: {
      headline: 'The picture in your answers is still forming — that is where to look.',
      body: desiredAnswer.length > 10
        ? `You described wanting to move toward: "${desiredAnswer}." The pattern in your answers suggests that some of what you do is moving and some is not. The question the pattern opens is which part of that picture the direction you named builds on — because in three years, the two sides of that picture will be in different places.`
        : 'The pattern in your answers suggests a picture that is mid-movement. Some of what you described is shifting with the broader landscape, some is in territory that does not transfer easily to systems. The question is which part of that picture you want to build toward, and whether the direction you are drawn to runs with that or against it.'
    },

    UNCLEAR: {
      headline: 'The most important part of the picture is still open.',
      body: 'The pattern the examination can surface from the territory you described is still forming — not because there is nothing there, but because the most important signals in the new economy do not live in role titles or sector categories. They live in how you actually work: what you notice first, what problems find you before you go looking, what judgment you exercise that is not in any framework you were handed. That is where the next version of this reading starts.'
    },

    UNCERTAIN: {
      headline: 'The reading continues with what you name specifically.',
      body: desiredAnswer.length > 10
        ? `You described wanting to move toward: "${desiredAnswer}." The pattern in your answers is still forming — what tends to bring it into focus is naming the specific things shifting in your actual work, and naming what you sense remains distinctly yours. Not categories. Specific things. The reading sharpens when those get named.`
        : 'The picture is still forming — that is fine. What tends to bring the pattern into focus is naming specifically what AI is already handling in your actual work, and specifically what you sense remains distinctly yours within it. Not categories. Specific things. The reading sharpens when those get named.'
    }
  }

  return directions[position] || directions.UNCERTAIN
}

/* ── MAIN EXPORT ─────────────────────────────────────────────────── */

export function interpret(payload, answers) {
  const out = payload?.output || payload
  const claude = out?.claudeReading || null

  const position = classifyPosition(out, answers)

  // When Claude reading is present, use it for all narrative sections.
  // Fall back to template logic when it is not available.
  const positionReading = claude?.positionReading || buildPositionReading(position, out, answers)

  const aiExposure = claude?.aiExposureItems
    ? claude.aiExposureItems.map(item => ({ ...item, source: item.source || 'engine' }))
    : buildAiExposure(out, answers)

  const defense = claude?.humanSignalItems
    ? claude.humanSignalItems.map(item => ({ ...item, source: item.source || 'engine' }))
    : buildDefense(out, answers)

  const strategicQuestions = buildStrategicQuestions(out)

  const direction = claude
    ? { headline: claude.directionHeadline || '', body: claude.directionBody || '' }
    : buildDirection(position, out, answers)

  const claudeActions = claude?.actions || []

  const conf = out?.confidence || {}
  const overall = (conf?.overall || 'low').toLowerCase()
  const hash = out?.deterministicReplayHash || ''

  return {
    position,
    positionReading,
    aiExposure,
    defense,
    strategicQuestions,
    direction,
    claudeActions,
    overall,
    hash,
    answers,
    hasClaude: !!claude,
  }
}

export const POSITION_LABELS = {
  DEFENDED: 'READING: ANCHORED',
  EXPOSED: 'READING: IN MOTION',
  PARTIAL: 'READING: FORMING',
  UNCLEAR: 'READING: OPEN',
  UNCERTAIN: 'READING: IN PROCESS'
}

export const POSITION_COLORS = {
  DEFENDED: 'rgba(120, 220, 160, .72)',
  EXPOSED: 'rgba(255, 140, 100, .72)',
  PARTIAL: 'rgba(185, 212, 255, .72)',
  UNCLEAR: 'rgba(242, 200, 100, .72)',
  UNCERTAIN: 'rgba(242, 240, 234, .32)'
}
